import { createContext, type Dispatch, type SetStateAction } from "react";
import * as z from "zod";

import { CaseCustomFilter } from "@/api/resources/cases/types";
import { MaintenanceCustomFilter } from "@/api/resources/maintenances/types";
import { CaseViewType } from "@/components/Case/types";
import { MaintenanceViewStatusType, MaintenanceViewType } from "@/components/Maintenance/types";
import { PartInventoryPickingViewType } from "@/components/PartInventory/types";
import { PartModelConstraintViewType } from "@/components/PartModelConstraint/types";
import { ServiceJobGanttViewType } from "@/components/ServiceJob/Gantt/serviceJobGanttViewType";
import { ServiceJobViewType } from "@/components/ServiceJob/types";
import { DailyOverviewViewType } from "@/components/Workspace/DailyOverview/types";
import { CaseStatus, type MaintenanceStatus, type CaseType } from "@/supabase.types";
import { getDateDaysInTheFuture, getDateEndOfDay, getDateStartOfDay } from "@/utils/dates";

import type { QueryFilter } from "@/utils/filter";

export type QueryFilters = {
  [
    key in keyof Omit<
      FilterOptions,
      | "caseCustomFilter"
      | "maintenanceCustomFilter"
      | "mDateFrom"
      | "mDateUntil"
      | "mDate"
      | "viewDaily"
      | "viewServiceJobs"
      | "viewServiceJobsGantt"
      | "viewCases"
      | "viewCasesStatus"
      | "viewMaintenances"
      | "viewMaintenancesStatus"
      | "viewPartPicking"
      | "viewPartConstraints"
    >
  ]: QueryFilter | undefined;
};

export interface FilterOptions {
  deployments: string[];
  users: string[];
  manufacturers: string[];
  deviceModels: string[];
  devices: string[];
  maintenanceStatus: MaintenanceStatus[];
  deviceTags: string[];
  caseTags: string[];
  caseTypes: CaseType[];
  caseStatus: CaseStatus[];
  caseCustomFilter: CaseCustomFilter | undefined;
  maintenanceTags: string[];
  maintenanceCustomFilter: MaintenanceCustomFilter | undefined;
  mDateFrom: string | null;
  mDateUntil: string | null;
  mDate: string | null;
  viewDaily: DailyOverviewViewType; // DailyOverview
  viewServiceJobs: ServiceJobViewType; // ServiceJobs
  viewServiceJobsGantt: ServiceJobGanttViewType; // ServiceJobs Gantt
  viewCases: CaseViewType; // Cases
  viewCasesStatus: CaseStatus; // Cases Status
  viewMaintenances: MaintenanceViewType; // Maintenances
  viewMaintenancesStatus: MaintenanceViewStatusType; // Maintenances Status
  viewPartPicking: PartInventoryPickingViewType; // Part Picking
  viewPartConstraints: PartModelConstraintViewType; // Part Constraints
}
export const defaultGlobalFilters: FilterOptions = {
  deployments: [] as string[],
  users: [] as string[],
  manufacturers: [] as string[],
  deviceModels: [] as string[],
  devices: [] as string[],
  maintenanceStatus: [] as MaintenanceStatus[],
  deviceTags: [] as string[],
  caseTags: [] as string[],
  caseTypes: [] as CaseType[],
  caseStatus: [] as CaseStatus[],
  caseCustomFilter: undefined as CaseCustomFilter | undefined,
  maintenanceTags: [] as string[],
  maintenanceCustomFilter: undefined as MaintenanceCustomFilter | undefined,
  mDateFrom: getDateStartOfDay(),
  mDateUntil: getDateEndOfDay(getDateDaysInTheFuture(30)),
  mDate: getDateEndOfDay(),
  viewDaily: DailyOverviewViewType.Tasks,
  viewServiceJobs: ServiceJobViewType.Calendar,
  viewServiceJobsGantt: ServiceJobGanttViewType.ServiceJobs,
  viewCases: CaseViewType.Kanban,
  viewCasesStatus: CaseStatus.reported,
  viewMaintenances: MaintenanceViewType.Calendar,
  viewMaintenancesStatus: MaintenanceViewStatusType.Open,
  viewPartPicking: PartInventoryPickingViewType.Requested,
  viewPartConstraints: PartModelConstraintViewType.Exceeded,
};

export const globalFiltersSet = Object.keys(defaultGlobalFilters) as (keyof FilterOptions)[];

export const globalFiltersSearchSchema = z.object({
  deployments: z.array(z.string()).default([]),
  users: z.array(z.string()).default([]),
  manufacturers: z.array(z.string()).default([]),
  deviceModels: z.array(z.string()).default([]),
  devices: z.array(z.string()).default([]),
  maintenanceStatus: z.array(z.string()).default([]),
  deviceTags: z.array(z.string()).default([]),
  caseTags: z.array(z.string()).default([]),
  caseTypes: z.array(z.string()).default([]),
  caseStatus: z.array(z.string()).default([]),
  caseCustomFilter: z.enum(CaseCustomFilter).optional(),
  maintenanceTags: z.array(z.string()).default([]),
  maintenanceCustomFilter: z.enum(MaintenanceCustomFilter).optional(),
  mDateFrom: z.string().nullable().default(getDateStartOfDay()),
  mDateUntil: z
    .string()
    .nullable()
    .default(getDateEndOfDay(getDateDaysInTheFuture(30))),
  mDate: z.string().nullable().default(getDateEndOfDay()),
  viewDaily: z.enum(DailyOverviewViewType).default(DailyOverviewViewType.Tasks),
  viewServiceJobs: z.enum(ServiceJobViewType).default(ServiceJobViewType.Calendar),
  viewServiceJobsGantt: z.enum(ServiceJobGanttViewType).default(ServiceJobGanttViewType.ServiceJobs),
  viewCases: z.enum(CaseViewType).default(CaseViewType.Kanban),
  viewCasesStatus: z.enum(CaseStatus).default(CaseStatus.reported),
  viewMaintenances: z.enum(MaintenanceViewType).default(MaintenanceViewType.Calendar),
  viewMaintenancesStatus: z.enum(MaintenanceViewStatusType).default(MaintenanceViewStatusType.Open),
  viewPartPicking: z.enum(PartInventoryPickingViewType).default(PartInventoryPickingViewType.Requested),
  viewPartConstraints: z.enum(PartModelConstraintViewType).default(PartModelConstraintViewType.Exceeded),
});

export const defaultGlobalFiltersContext = {
  expanded: false,
  optionsApplied: false,
  toggle: () => {},
  close: () => {},
  resetOptions: () => {},
  options: defaultGlobalFilters,
  setOptions: () => {},
  queryFilters: {
    deployments: undefined,
    users: undefined,
    manufacturers: undefined,
    deviceModels: undefined,
    devices: undefined,
    maintenanceStatus: undefined,
    deviceTags: undefined,
    caseTags: undefined,
    caseTypes: undefined,
    caseStatus: undefined,
    caseCustomFilter: undefined,
    maintenanceTags: undefined,
    maintenanceCustomFilter: undefined,
    mDateFrom: getDateStartOfDay(),
    mDateUntil: getDateEndOfDay(getDateDaysInTheFuture(30)),
    mDate: getDateEndOfDay(),
    viewDaily: DailyOverviewViewType.Tasks,
    viewServiceJobs: ServiceJobViewType.Calendar,
    viewServiceJobsGantt: ServiceJobGanttViewType.ServiceJobs,
    viewCases: CaseViewType.Kanban,
    viewCasesStatus: CaseStatus.reported,
    viewMaintenances: MaintenanceViewType.Calendar,
    viewMaintenancesStatus: MaintenanceViewStatusType.Open,
    viewPartPicking: PartInventoryPickingViewType.Requested,
    viewPartConstraints: PartModelConstraintViewType.Exceeded,
  },
  queryFilterAddons: {
    deviceTagsNegation: [],
    deviceTagsChildrenDevices: [],
    deviceTagsChildrenDevicesInArray: [],
    deviceTagsChildrenAssembledParts: [],
    deviceTagsNegationAssembledParts: [],
    deviceModelsAssembledParts: [],
    inventoryParts: [],
    incomingParts: [],
  },
};

export const GlobalFiltersContext = createContext<{
  expanded: boolean;
  optionsApplied: boolean;
  toggle: () => void;
  close: () => void;
  resetOptions: () => void;
  options: FilterOptions;
  setOptions: Dispatch<SetStateAction<FilterOptions>>;
  queryFilters: QueryFilters;
}>(defaultGlobalFiltersContext);
